import { ETextStyleVariant, Text } from '@outdoorsyco/bonfire';
import React from 'react';
import { useIntl } from 'react-intl';

import { PriceType } from '@/redux/selectors/listing/page';
import { formatCurrency } from '@/utility/currency';

import { PriceSize } from '../Price';
import css from './PriceCurrent.module.css';

type ICurrentPriceElement = React.HTMLAttributes<HTMLElement>;

interface IProps {
  currency: string;
  currentPrice: number;
  period: PriceType;
  size?: PriceSize;
  testId?: string;
  isCampsite?: boolean;
  isRVBundle?: boolean;
  nights?: number;
  totalPrice?: number;
  isMapPopup?: boolean;
  originalTotalPrice?: number;
  showDiscount?: boolean;
  hasDiscount?: boolean;
}

const priceTemplate = (value: React.ReactNode): React.ReactElement => (
  <span aria-hidden className={css.price}>
    {value}
  </span>
);

const periodTemplate = (period: React.ReactNode): React.ReactElement => (
  <span aria-hidden className={css.period}>
    {period}
  </span>
);

const discountPriceTemplate = (value: React.ReactNode): React.ReactElement => (
  <span aria-hidden className={css.originalTotalPrice}>
    {value}
  </span>
);

const PriceCurrent: React.FC<IProps & ICurrentPriceElement> = ({
  size = PriceSize.medium,
  currency,
  currentPrice,
  period,
  testId = 'price-current',
  isCampsite = false,
  isRVBundle = false,
  nights,
  totalPrice,
  isMapPopup,
  originalTotalPrice,
  showDiscount,
  hasDiscount,
}) => {
  const intl = useIntl();

  // Format the appropriate price based on the context
  const formattedPrice = formatCurrency({
    currency,
    digits: 0,
    maxDigits: 0,
    priceInCents: totalPrice ? totalPrice : currentPrice,
    ...(isRVBundle && { ceil: true }),
  });

  const formattedOriginalTotalPrice = formatCurrency({
    currency,
    digits: 0,
    maxDigits: 0,
    priceInCents: originalTotalPrice,
    ...(isRVBundle && { ceil: true }),
  });

  const screenReaderMessage = intl.formatMessage(
    {
      description: '[Price]: screen reader message for current price per period (day/night)',
      defaultMessage: '{currentPrice} per {period}',
      id: 'RgOVlk',
    },
    {
      currentPrice: formattedPrice,
      period: period,
    },
  );

  const renderPrice = () => {
    if (typeof nights === 'number' && nights > 0) {
      if (isMapPopup) {
        if (showDiscount && hasDiscount) {
          return intl.formatMessage(
            {
              description: '[Price]: current price formatting for a listing',
              defaultMessage:
                '<discountPriceTemplate>{originalTotalPrice}</discountPriceTemplate><priceTemplate>{value}</priceTemplate><periodTemplate>for {nights, plural, one {# night} other {# nights}}</periodTemplate>',
              id: '+X3Ex0',
            },
            {
              discountPriceTemplate: discountPriceTemplate,
              priceTemplate: priceTemplate,
              periodTemplate: isCampsite ? priceTemplate : periodTemplate,
              nights: nights,
              value: isRVBundle ? `RV + Site ${formattedPrice}` : formattedPrice,
              originalTotalPrice: isRVBundle
                ? `RV + Site ${formattedOriginalTotalPrice}`
                : formattedOriginalTotalPrice,
            },
          );
        }

        return intl.formatMessage(
          {
            description: '[Price]: current price formatting for a listing',
            defaultMessage:
              '<priceTemplate>{value}</priceTemplate><periodTemplate>for {nights, plural, one {# night} other {# nights}}</periodTemplate>',
            id: 'aIZeRu',
          },
          {
            priceTemplate: priceTemplate,
            periodTemplate: isCampsite ? priceTemplate : periodTemplate,
            nights: nights,
            value: isRVBundle ? `RV + Site ${formattedPrice}` : formattedPrice,
          },
        );
      }

      return (
        <div className="flex order-1 truncate gap-1">
          {showDiscount && hasDiscount && (
            <Text
              aria-hidden
              variant={ETextStyleVariant.LargeBold}
              className="line-through text-[#909398]">
              {isRVBundle
                ? `RV + Site ${formattedOriginalTotalPrice}`
                : formattedOriginalTotalPrice}
            </Text>
          )}
          <Text aria-hidden variant={ETextStyleVariant.LargeBold}>
            {isRVBundle ? `RV + Site ${formattedPrice}` : formattedPrice}
          </Text>
          <Text aria-hidden variant={ETextStyleVariant.LargeRegular} className="truncate">
            {intl.formatMessage(
              {
                defaultMessage: 'for {nights, plural, one {# night} other {# nights}}',
                id: 'jzRwv5',
                description: '[Price]: total price formatting for a listing',
              },
              {
                nights: nights,
              },
            )}
          </Text>
        </div>
      );
    }

    return intl.formatMessage(
      {
        description: '[Price]: current price formatting for a listing',
        defaultMessage:
          '<priceTemplate>{value}</priceTemplate><periodTemplate>/{period, select, day {day} night {night} other {}}</periodTemplate>',
        id: 'eJ7KW9',
      },
      {
        priceTemplate: priceTemplate,
        periodTemplate: isCampsite ? priceTemplate : periodTemplate,
        period: period?.toLowerCase(),
        value: isRVBundle ? `RV + Site ${formattedPrice}` : formattedPrice,
      },
    );
  };

  return (
    <p
      className={css.container}
      aria-label={screenReaderMessage}
      data-testid={testId}
      data-size={size}>
      {renderPrice()}
    </p>
  );
};

export default PriceCurrent;
